import { Component } from '@theme/component';
import { debounce, isClickedOutside, lockScroll, onAnimationEnd, unlockScroll } from '@theme/utilities';
import { getScrollTop, scrollTo } from '@theme/scroll-container';

/**
 * A custom element that manages a dialog.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog – The dialog element.
 *
 * @extends Component<Refs>
 */
export class DialogComponent extends Component {
  requiredRefs = ['dialog'];

  connectedCallback() {
    super.connectedCallback();

    this.refs.dialog.addEventListener('cancel', this.#handleCancel);
    this.refs.dialog.addEventListener('close', this.#handleNativeClose);

    if (this.minWidth || this.maxWidth) {
      window.addEventListener('resize', this.#handleResize);
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    cancelAnimationFrame(this.#openingFrame);
    this.#openingFrame = 0;
    this.#closeGeneration += 1;
    this.#managedOpen = false;
    this.#isClosing = false;
    this.removeEventListener('click', this.#handleClick);
    this.removeEventListener('keydown', this.#handleKeyDown);
    this.refs.dialog.removeEventListener('cancel', this.#handleCancel);
    this.refs.dialog.removeEventListener('close', this.#handleNativeClose);
    if (this.minWidth || this.maxWidth) {
      window.removeEventListener('resize', this.#handleResize);
    }
    unlockScroll(this.refs.dialog);
  }

  #handleResize = debounce(() => {
    const { minWidth, maxWidth } = this;

    if (!minWidth && !maxWidth) return;

    const windowWidth = window.innerWidth;
    if (windowWidth < minWidth || windowWidth > maxWidth) {
      this.closeDialog();
    }
  }, 50);

  #previousScrollY = 0;
  #openingFrame = 0;
  #managedOpen = false;
  #isClosing = false;
  #closeGeneration = 0;
  #jellyfitGuideLoad = null;

  /** Load the optional unit switch only after its size guide is opened. */
  #loadJellyfitGuide() {
    const src = this.dataset.jellyfitGuideModule;
    const { dialog } = this.refs;
    if (!src || !dialog.querySelector('.nh-jellyfit-guide')) return;

    const request = this.#jellyfitGuideLoad?.src === src
      ? this.#jellyfitGuideLoad
      : { src, promise: import(src) };
    this.#jellyfitGuideLoad = request;

    request.promise.then(({ initJellyfitSizeGuides }) => {
      if (this.isConnected && this.refs.dialog === dialog && dialog.open &&
          this.dataset.jellyfitGuideModule === src) {
        initJellyfitSizeGuides(dialog);
      }
    }).catch(() => {
      // Keep the initial centimetre table readable; retry on a later opening.
    }).finally(() => {
      if (this.#jellyfitGuideLoad === request) this.#jellyfitGuideLoad = null;
    });
  }

  /** @param {Event} event */
  #handleCancel = (event) => {
    if (event.target !== this.refs.dialog || event.defaultPrevented) return;
    event.preventDefault();
    this.closeDialog();
  };

  /** @param {Event} event */
  #handleNativeClose = (event) => {
    const { dialog } = this.refs;
    // Drawer restacking closes and reopens a guide synchronously. Its queued
    // close event must not release the lock of the now-open guide.
    if (event.target !== dialog || dialog.open) return;
    this.#finishClose();
  };

  /** Complete both animated and native closure exactly once. */
  #finishClose() {
    if (!this.#managedOpen) return;
    this.#managedOpen = false;
    this.#isClosing = false;
    this.#closeGeneration += 1;

    const { dialog } = this.refs;
    this.removeEventListener('click', this.#handleClick);
    this.removeEventListener('keydown', this.#handleKeyDown);
    unlockScroll(dialog);
    if (dialog.open) dialog.close();
    dialog.classList.remove('dialog-closing');
    scrollTo({ top: this.#previousScrollY, behavior: 'instant' });
    this.dispatchEvent(new DialogCloseEvent());
  }

  /**
   * Shows the dialog.
   */
  showDialog() {
    const { dialog } = this.refs;

    if (dialog.open || this.#openingFrame) return;

    this.#previousScrollY = getScrollTop();

    // Prevent layout thrashing by separating DOM reads from DOM writes
    this.#openingFrame = requestAnimationFrame(() => {
      this.#openingFrame = 0;
      if (!this.isConnected || dialog.open) return;
      this.#closeGeneration += 1;
      this.#isClosing = false;
      dialog.classList.remove('dialog-closing');
      this.#managedOpen = true;
      lockScroll(dialog);

      dialog.showModal();
      this.#loadJellyfitGuide();
      this.dispatchEvent(new DialogOpenEvent());

      this.addEventListener('click', this.#handleClick);
      this.addEventListener('keydown', this.#handleKeyDown);
    });
  }

  /**
   * Closes the dialog.
   */
  closeDialog = async () => {
    const { dialog } = this.refs;

    cancelAnimationFrame(this.#openingFrame);
    this.#openingFrame = 0;
    if (!dialog.open) {
      this.#finishClose();
      return;
    }
    if (this.#isClosing) return;
    this.#isClosing = true;
    const generation = ++this.#closeGeneration;

    this.removeEventListener('click', this.#handleClick);
    this.removeEventListener('keydown', this.#handleKeyDown);

    // Force browser to restart animation by resetting it
    // Temporarily remove any existing animation state
    dialog.style.animation = 'none';

    // Force a reflow
    void dialog.offsetWidth;

    // Now add the closing class and restore animation
    dialog.classList.add('dialog-closing');
    dialog.style.animation = '';

    await onAnimationEnd(dialog, undefined, {
      subtree: false,
    });

    if (generation === this.#closeGeneration) this.#finishClose();
  };

  /**
   * Toggles the dialog.
   */
  toggleDialog = () => {
    if (this.refs.dialog.open) {
      this.closeDialog();
    } else {
      this.showDialog();
    }
  };

  /**
   * Closes the dialog when the user clicks outside of it.
   *
   * @param {MouseEvent} event - The mouse event.
   */
  #handleClick(event) {
    const { dialog } = this.refs;

    if (isClickedOutside(event, dialog)) {
      this.closeDialog();
    }
  }

  /**
   * Closes the dialog when the user presses the escape key.
   *
   * @param {KeyboardEvent} event - The keyboard event.
   */
  #handleKeyDown(event) {
    if (event.key !== 'Escape' || event.defaultPrevented || event.isComposing) return;
    const eventDialog = event.target instanceof Element ? event.target.closest('dialog') : null;
    if (eventDialog && eventDialog !== this.refs.dialog) return;

    event.preventDefault();
    this.closeDialog();
  }

  /**
   * Gets the minimum width of the dialog.
   *
   * @returns {number} The minimum width of the dialog.
   */
  get minWidth() {
    return Number(this.getAttribute('dialog-active-min-width'));
  }

  /**
   * Gets the maximum width of the dialog.
   *
   * @returns {number} The maximum width of the dialog.
   */
  get maxWidth() {
    return Number(this.getAttribute('dialog-active-max-width'));
  }
}

if (!customElements.get('dialog-component')) customElements.define('dialog-component', DialogComponent);

export class DialogOpenEvent extends CustomEvent {
  constructor() {
    super(DialogOpenEvent.eventName);
  }

  static eventName = 'dialog:open';
}

export class DialogCloseEvent extends CustomEvent {
  constructor() {
    super(DialogCloseEvent.eventName);
  }

  static eventName = 'dialog:close';
}

document.addEventListener(
  'toggle',
  (event) => {
    if (event.target instanceof HTMLDetailsElement) {
      if (event.target.hasAttribute('scroll-lock')) {
        const { open } = event.target;
        if (open) {
          lockScroll(event.target);
        } else {
          unlockScroll(event.target);
        }
      }
    }
  },
  { capture: true }
);
